import {
  Button,
  Checkbox,
  Flex,
  Table,
  Tag,
  Tooltip,
  type TableColumnType,
  message,
} from 'antd';
import { format } from 'date-fns';
import { FC, useEffect, useMemo, useState } from 'react';

import { ArchivedTag } from '@components';
import { captureException } from '@sentry/react';

import { type TransactionBatch, useTransactionBatches } from '@hooks-api';

import TransactionBatchFileModal from './TransactionBatchFileModal';
import { formatBookingDay } from './bookingDate';
import * as S from './styled';

type Props = {
  companyId?: string;
};

const SENTRY_TAGS = { feature: 'transaction-batches' } as const;

const buildFileName = (batch: TransactionBatch): string => {
  const account =
    batch.account?.accountName?.replace(/[^a-zA-Z0-9]/g, '_') || 'transactions';

  return `${account}_${format(new Date(batch.createdAt), 'yyyy-MM-dd')}.csv`;
};

const TransactionBatches: FC<Props> = ({ companyId }) => {
  // Off by default: the bank sync records a batch on every poll and most of them import
  // nothing, so including them buries the batches that actually brought transactions in.
  const [includeEmpty, setIncludeEmpty] = useState(false);
  // Also off by default — an archived account is usually a replaced duplicate, and its
  // batches sit right next to the live account's under the very same name [ENG-1211].
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data, isPending, isError, error } = useTransactionBatches(companyId, {
    includeEmpty,
  });
  const [selected, setSelected] = useState<TransactionBatch | null>(null);

  useEffect(() => {
    if (!isError) {
      return;
    }

    message.error('Failed to load uploaded batches. Please try again.');
    captureException(error, { level: 'error', tags: SENTRY_TAGS });
  }, [isError, error]);

  // Filtered client-side: the flag rides along on every row, so hiding them costs no
  // extra request and the toggle stays instant.
  const rows = useMemo(
    () =>
      includeArchived
        ? data
        : data?.filter((batch) => !batch.account?.isArchived),
    [data, includeArchived]
  );

  // Derived from the loaded rows so the options only list banks that are actually present.
  // Ant Design keeps the option list stable while a filter is applied.
  const bankFilters = useMemo(() => {
    const names = new Set(
      (rows ?? [])
        .map((batch) => batch.bank?.name)
        .filter((name): name is string => Boolean(name))
    );

    return [...names]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ text: name, value: name }));
  }, [rows]);

  const columns: TableColumnType<TransactionBatch>[] = [
    {
      key: 'createdAt',
      dataIndex: 'createdAt',
      title: 'Uploaded',
      width: 120,
      render: (createdAt) => format(new Date(createdAt), 'dd.MM.yyyy'),
    },
    {
      key: 'source',
      dataIndex: 'source',
      title: 'Type',
      width: 130,
      filters: [
        { text: 'Integration', value: 'integration' },
        { text: 'CSV upload', value: 'csv' },
      ],
      onFilter: (value, batch) => batch.source === value,
      render: (source: TransactionBatch['source']) =>
        source === 'integration' ? (
          <Tag color="blue">Integration</Tag>
        ) : (
          <Tag color="green">CSV upload</Tag>
        ),
    },
    {
      key: 'bank',
      dataIndex: 'bank',
      title: 'Bank',
      width: 150,
      ellipsis: true,
      filters: bankFilters,
      onFilter: (value, batch) => batch.bank?.name === value,
      render: (bank: TransactionBatch['bank']) => bank?.name || '—',
    },
    {
      key: 'account',
      dataIndex: 'account',
      title: 'Bank account',
      ellipsis: true,
      render: (account: TransactionBatch['account']) => (
        <Flex align="center" gap={8}>
          <S.AccountName>
            {account?.accountName || account?.accountNumber || '—'}
          </S.AccountName>
          {account?.isArchived && <ArchivedTag />}
        </Flex>
      ),
    },
    {
      key: 'dateRange',
      title: 'Date range',
      width: 200,
      render: (_, batch) =>
        `${formatBookingDay(batch.dateFrom)} — ${formatBookingDay(batch.dateTo)}`,
    },
    {
      key: 'uploadedTransactionsCount',
      dataIndex: 'uploadedTransactionsCount',
      title: 'Uploaded rows',
      align: 'right',
      width: 130,
    },
    {
      key: 'currentTransactionsCount',
      dataIndex: 'currentTransactionsCount',
      title: 'Current rows',
      align: 'right',
      width: 130,
    },
    {
      key: 'actions',
      title: '',
      width: 150,
      align: 'right',
      render: (_, batch) => (
        <Button type="link" onClick={() => setSelected(batch)}>
          View / download
        </Button>
      ),
    },
  ];

  return (
    <>
      <S.Toolbar>
        <Tooltip title="An archived account is hidden from the client portal, but its batches and transactions are still here.">
          <Checkbox
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          >
            Show batches from archived accounts
          </Checkbox>
        </Tooltip>

        <Tooltip title="The bank sync records a batch on every poll, including the ones that imported nothing.">
          <Checkbox
            checked={includeEmpty}
            onChange={(e) => setIncludeEmpty(e.target.checked)}
          >
            Show batches with no transactions
          </Checkbox>
        </Tooltip>
      </S.Toolbar>

      <Table<TransactionBatch>
        rowKey={({ id }) => id}
        dataSource={rows}
        columns={columns}
        loading={isPending}
        pagination={false}
        // The fixed column widths already add up to 1010px; anything less and antd's fixed
        // layout squeezes the one flexible column (Bank account) towards zero.
        scroll={{ x: 1200 }}
      />

      <TransactionBatchFileModal
        open={selected !== null}
        batch={selected}
        companyId={companyId}
        fileName={selected ? buildFileName(selected) : ''}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

export default TransactionBatches;
